<template>
  <v-data-table
    :headers="locationHeaders"
    :items="deviceLocations"
    :options.sync="deviceLocationOptions"
    :server-items-length="totalDeviceLocations"
    :loading="loading"
    :items-per-page="5"
    :footer-props="{
      showFirstLastPage: true,
      'items-per-page-options': [5, 10, 15],
    }"
  >
    <template v-slot:[`item.coordinates`]="{ item }">
      <a
        v-if="mapUrl(item)"
        :href="mapUrl(item)"
        target="_blank"
        rel="noopener noreferrer"
        class="device-location-link"
        :title="mapUrl(item)"
      >
        <v-icon x-small color="primary" class="mr-1">mdi-map-marker</v-icon>
        {{ item.latitude }}, {{ item.longitude }}
      </a>
      <span v-else class="grey--text">No coordinates</span>
    </template>

    <template v-slot:[`item.time`]="{ item }">
      <div>{{ $format.dateTime(item.time) }}</div>
    </template>

    <template v-slot:[`item.map`]="{ item }">
      <v-btn
        v-if="mapUrl(item)"
        icon
        small
        :href="mapUrl(item)"
        target="_blank"
        rel="noopener noreferrer"
        title="Open this point in Google Maps"
      >
        <v-icon small color="primary">mdi-open-in-new</v-icon>
      </v-btn>
    </template>

    <template v-slot:top>
      <div
        v-if="routeUrl"
        class="d-flex justify-end pb-2"
      >
        <v-btn
          small
          text
          color="primary"
          :href="routeUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          <v-icon small class="mr-1">mdi-map-marker-path</v-icon>
          Open these {{ deviceLocations.length }} points in Google Maps
        </v-btn>
      </div>
    </template>
  </v-data-table>
</template>

<script>
export default {
  props: ["currentMobileStatus"],
  data: () => ({
    // Sorting is fixed newest-first, so the headers offer no sort affordance.
    locationHeaders: [
      { text: "Coordinates", value: "coordinates", sortable: false },
      { text: "Time", value: "time", sortable: false },
      { text: "", value: "map", sortable: false, align: "end", width: "56px" },
    ],
    totalDeviceLocations: 0,
    deviceLocations: [],
    deviceLocationOptions: {},
    loading: false,
    knownTotal: null,
  }),
  watch: {
    deviceLocationOptions: {
      handler(val) {
        if (val) {
          this.getDeviceLocations();
        }
      },
      deep: true,
    },
    currentMobileStatus() {
      this.knownTotal = null;
    },
  },
  computed: {
    // Google Maps caps a directions url at ~10 stops, so only offer the route
    // link when the visible page fits inside that.
    routeUrl() {
      const points = this.deviceLocations
        .filter((item) => this.hasCoordinates(item))
        .map((item) => `${item.latitude},${item.longitude}`);

      if (points.length < 2 || points.length > 10) {
        return "";
      }

      return `https://www.google.com/maps/dir/${points.join("/")}`;
    },
  },
  methods: {
    hasCoordinates(item) {
      return (
        item &&
        item.latitude !== null &&
        item.latitude !== undefined &&
        item.latitude !== "" &&
        item.longitude !== null &&
        item.longitude !== undefined &&
        item.longitude !== ""
      );
    },

    mapUrl(item) {
      if (!this.hasCoordinates(item)) {
        return "";
      }
      const query = encodeURIComponent(`${item.latitude},${item.longitude}`);
      return `https://www.google.com/maps/search/?api=1&query=${query}`;
    },

    fetchAscendingPage(page, limit) {
      return this.$http("Device/GetDeviceLocations", {
        params: {
          mobileStatusId: this.currentMobileStatus,
          page: page,
          limit: limit,
        },
      }).then((response) => ({
        total: Number(response.data.total) || 0,
        rows: Array.isArray(response.data.data) ? response.data.data : [],
      }));
    },

    // The endpoint returns locations oldest-first and takes no sort parameter,
    // so walk its pages backwards: map the requested newest-first page onto the
    // matching ascending window, fetch only the page(s) that window spans, then
    // reverse it. Keeps server paging instead of pulling the whole history.
    async getDeviceLocations() {
      const { page, itemsPerPage } = this.deviceLocationOptions;
      const currentPage = page > 0 ? page : 1;
      const limit = itemsPerPage > 0 ? itemsPerPage : 5;

      this.loading = true;

      try {
        if (this.knownTotal === null) {
          const probe = await this.fetchAscendingPage(1, limit);
          this.knownTotal = probe.total;
          this.totalDeviceLocations = probe.total;
        }

        const total = this.knownTotal;
        const lastIndex = total - (currentPage - 1) * limit - 1;
        const firstIndex = Math.max(0, total - currentPage * limit);

        if (!total || lastIndex < 0) {
          this.deviceLocations = [];
          return;
        }

        // The window can straddle two ascending pages when the total is not a
        // clean multiple of the page size.
        const firstPage = Math.floor(firstIndex / limit) + 1;
        const lastPage = Math.floor(lastIndex / limit) + 1;

        let rows = [];
        let latestTotal = total;

        for (let page = firstPage; page <= lastPage; page += 1) {
          const result = await this.fetchAscendingPage(page, limit);
          rows = rows.concat(result.rows);
          latestTotal = result.total;
        }

        const offset = firstIndex - (firstPage - 1) * limit;
        this.deviceLocations = rows
          .slice(offset, offset + (lastIndex - firstIndex + 1))
          .reverse();

        // Keep the count fresh for the next page turn.
        this.knownTotal = latestTotal;
        this.totalDeviceLocations = latestTotal;
      } catch (error) {
        this.deviceLocations = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.device-location-link {
  font-family: monospace;
  text-decoration: none;
}

.device-location-link:hover {
  text-decoration: underline;
}
</style>
